import { KeyRound, Laptop, Monitor, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Alert, Button, Card, Modal, Spinner } from "@/components/common";
import { PasswordInput } from "@/components/common/PasswordInput";
import {
  useChangePassword,
  useRemoveTrustedDevice,
  useRevokeOtherSessions,
  useRevokeSession,
  useSessions,
  useTrustedDevices,
} from "@/hooks/useSettings";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import { SessionItem } from "./SessionItem";

export function SecurityTab() {
  return (
    <div className="space-y-6">
      <ChangePasswordSection />
      <TwoFactorSection />
      <TrustedDevicesSection />
      <SessionsSection />
    </div>
  );
}

function ChangePasswordSection() {
  const changePassword = useChangePassword();
  const [isOpen, setIsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);

  const confirmError =
    confirmPassword && newPassword !== confirmPassword
      ? "Hasła nie są identyczne"
      : undefined;

  function resetForm() {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setSuccess(false);
    changePassword.reset();
  }

  function handleClose() {
    setIsOpen(false);
    resetForm();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      return;
    }

    changePassword.mutate(
      { currentPassword, newPassword, confirmPassword },
      {
        onSuccess: () => {
          setSuccess(true);
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
        },
      },
    );
  }

  return (
    <>
      <Card>
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-adara-50 p-2 text-adara-700 dark:bg-adara-900/30 dark:text-adara-300">
            <KeyRound size={20} aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-main">Zmiana hasła</h3>
            <p className="mt-1 text-xs text-dim">
              Regularnie zmieniaj hasło, aby chronić swoje konto
            </p>
          </div>
          <Button size="sm" onClick={() => setIsOpen(true)}>
            Zmień hasło
          </Button>
        </div>
      </Card>

      <Modal isOpen={isOpen} onClose={handleClose} title="Zmiana hasła">
        <div className="space-y-4">
          {success && (
            <Alert
              variant="success"
              onDismiss={() => {
                setSuccess(false);
              }}
            >
              Hasło zostało zmienione
            </Alert>
          )}

          {changePassword.isError && (
            <Alert variant="error">
              {(changePassword.error as { message?: string })?.message ??
                "Nie udało się zmienić hasła"}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <PasswordInput
              label="Aktualne hasło"
              value={currentPassword}
              onChange={(e) => {
                setSuccess(false);
                changePassword.reset();
                setCurrentPassword(e.target.value);
              }}
              required
            />

            <div>
              <PasswordInput
                label="Nowe hasło"
                value={newPassword}
                onChange={(e) => {
                  setSuccess(false);
                  setNewPassword(e.target.value);
                }}
                required
              />
              <PasswordStrengthIndicator password={newPassword} />
            </div>

            <PasswordInput
              label="Potwierdź nowe hasło"
              value={confirmPassword}
              onChange={(e) => {
                setSuccess(false);
                setConfirmPassword(e.target.value);
              }}
              error={confirmError}
              required
            />

            <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
              <Button type="button" variant="ghost" onClick={handleClose}>
                Anuluj
              </Button>
              <Button
                type="submit"
                isLoading={changePassword.isPending}
                disabled={
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  !!confirmError
                }
              >
                Zmień hasło
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}

function TwoFactorSection() {
  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-adara-50 p-2 text-adara-700 dark:bg-adara-900/30 dark:text-adara-300">
          <ShieldCheck size={20} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-main">
            Uwierzytelnianie dwuskładnikowe (2FA)
          </h3>
          <p className="mt-1 text-xs text-dim">
            Każde logowanie potwierdzasz 6-cyfrowym kodem wysłanym na Twój
            adres e-mail. Ta ochrona jest zawsze włączona.
          </p>
        </div>
      </div>
    </Card>
  );
}

const dateFormat = new Intl.DateTimeFormat("pl-PL", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function TrustedDevicesSection() {
  const { data: devices, isLoading } = useTrustedDevices();
  const removeDevice = useRemoveTrustedDevice();

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-adara-50 p-2 text-adara-700 dark:bg-adara-900/30 dark:text-adara-300">
          <Laptop size={20} aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-main">
            Zaufane urządzenia
          </h3>
          <p className="mt-1 text-xs text-dim">
            Na tych przeglądarkach nie prosimy o kod z e-maila przez 90 dni.
            Zmiana hasła usuwa je wszystkie.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {devices?.map((device) => (
            <div
              key={device.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-edge-soft px-4 py-3"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-main">
                    {device.device} &middot; {device.browser}
                  </span>
                  {device.isCurrent && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                      To urządzenie
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-dim">
                  {device.ip ?? "IP nieznane"} &middot; dodane{" "}
                  {dateFormat.format(new Date(device.createdAt))} &middot; ważne
                  do {dateFormat.format(new Date(device.expiresAt))}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  removeDevice.mutate(device.id);
                }}
                disabled={removeDevice.isPending}
                className="shrink-0 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                Usuń
              </Button>
            </div>
          ))}

          {devices && devices.length === 0 && (
            <p className="py-4 text-center text-sm text-dim">
              Brak zaufanych urządzeń — przy logowaniu zaznacz „Zaufaj temu
              urządzeniu na 90 dni”.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function SessionsSection() {
  const { data: sessions, isLoading } = useSessions();
  const revokeSession = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      </Card>
    );
  }

  const otherSessions = sessions?.filter((s) => !s.isCurrent) ?? [];

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-adara-50 p-2 text-adara-700 dark:bg-adara-900/30 dark:text-adara-300">
            <Monitor size={20} aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-main">Aktywne sesje</h3>
            <p className="mt-1 text-xs text-dim">
              Zarządzaj urządzeniami zalogowanymi na Twoje konto
            </p>
          </div>
        </div>
        {otherSessions.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              revokeOthers.mutate();
            }}
            isLoading={revokeOthers.isPending}
            className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            Wyloguj inne sesje
          </Button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {sessions?.map((session) => (
          <SessionItem
            key={session.id}
            session={session}
            onRevoke={(id) => {
              revokeSession.mutate(id);
            }}
            isRevoking={revokeSession.isPending}
          />
        ))}
      </div>

      {sessions && sessions.length === 0 && (
        <p className="py-4 text-center text-sm text-dim">
          Brak aktywnych sesji
        </p>
      )}
    </Card>
  );
}
